import {
  PITCH,
  startKickoff,
  startRestart,
  type MatchState,
  type SimConfig,
  type TeamIdx,
  type TeamSize,
} from '@fafi/shared';

// D128 — `@fafi/scenarios`: the practice drills.
//
// IP §2.1 (IP:107) has named this package since M0 — "DATA-ONLY workspace package (penalty, corner,
// brawl, 1v1-keeper, …)" — and it was never built, which is why the user's ask ("the kickabout should
// allow for trying out standard situations so we can test penalties and free kicks") had nowhere to go.
//
// Every drill is a PURE function that mutates a `MatchState` by composing EXISTING shared exports
// (`startRestart`, `startKickoff`, direct placement). No I/O, no wall clock, no rng of its own — so
// "same seed + same drill ⇒ same state" is a testable property, and a drill exercises the SHIPPED
// restart machinery (D122–D124) rather than a parallel practice-only mode.

export type DrillId = 'penalty' | 'penalty-save' | 'fk-center' | 'fk-wide' | 'corner' | 'kickoff';

export interface DrillSpec {
  readonly id: DrillId;
  readonly label: string;
  readonly blurb: string;
  readonly teamSize: TeamSize;
  /** Which side runs as bots. 'defense' = you attack; 'both' = a full live rehearsal. */
  readonly bots: 'defense' | 'both';
  /** The team the local player is on. */
  readonly playerTeam: TeamIdx;
  /** D143 — which player the human drives (see `LessonSpec.playerRole`). Default `'outfield'`. */
  readonly playerRole?: 'gk' | 'outfield';
  /** Set the board up. Mutates `state`; must stay pure and deterministic. */
  apply(state: MatchState, cfg: SimConfig): void;
}

/** Attacking direction sign for `team` — +1 means it attacks +x. */
const dirOf = (s: MatchState, team: TeamIdx): number => s.attackDir[team]!;

/** Spread a team's outfielders across their own half so a drill never starts in a heap. */
function scatter(s: MatchState, team: TeamIdx, xFrac: number): void {
  const pitch = PITCH[s.mode.teamSize];
  const dir = dirOf(s, team);
  const outfield = s.players.filter((p) => p.team === team && !p.isGk);
  outfield.forEach((p, i) => {
    const lane = outfield.length > 1 ? (i / (outfield.length - 1)) * 2 - 1 : 0;
    p.pos = { x: dir * pitch.length * xFrac, y: lane * pitch.width * 0.3 };
    p.vel = { x: 0, y: 0 };
    p.action = 'IDLE';
    p.actionTicks = 0;
    p.charge = null;
  });
}

/** Put each keeper on his line. */
function keepersHome(s: MatchState): void {
  const pitch = PITCH[s.mode.teamSize];
  for (const p of s.players) {
    if (!p.isGk) continue;
    p.pos = { x: (-dirOf(s, p.team) * pitch.length) / 2 + dirOf(s, p.team) * 0.4, y: 0 };
    p.vel = { x: 0, y: 0 };
    p.action = 'IDLE';
    p.actionTicks = 0;
  }
}

/** A free kick for `team` at `dist` metres from the opposition goal, `y` off centre. */
function freeKickAt(s: MatchState, cfg: SimConfig, team: TeamIdx, dist: number, y: number): void {
  const pitch = PITCH[s.mode.teamSize];
  const dir = dirOf(s, team);
  keepersHome(s);
  scatter(s, team, 0.1);
  scatter(s, team === 0 ? 1 : 0, -0.28);
  startRestart(s, [], cfg, 'FREE_KICK', team, { x: dir * (pitch.length / 2 - dist), y });
}

const spec = (
  id: DrillId,
  label: string,
  blurb: string,
  teamSize: TeamSize,
  bots: DrillSpec['bots'],
  playerTeam: TeamIdx,
  apply: DrillSpec['apply'],
): DrillSpec => ({ id, label, blurb, teamSize, bots, playerTeam, apply });

export const DRILLS: readonly DrillSpec[] = [
  spec('penalty', 'Penalty', 'You take it. Walk up and pick your corner.', 5, 'defense', 0, (s, cfg) => {
    const pitch = PITCH[s.mode.teamSize];
    const dir = dirOf(s, 0);
    keepersHome(s);
    scatter(s, 0, 0.05);
    scatter(s, 1, -0.05);
    startRestart(s, [], cfg, 'PENALTY', 0, { x: dir * (pitch.length / 2 - pitch.penaltySpotDist), y: 0 });
  }),

  spec('penalty-save', 'Penalty (save it)', 'You keep. Bind your dive keys first — they ship unbound.', 5, 'both', 1, (s, cfg) => {
    const pitch = PITCH[s.mode.teamSize];
    const dir = dirOf(s, 0);
    keepersHome(s);
    scatter(s, 0, 0.05);
    scatter(s, 1, -0.05);
    startRestart(s, [], cfg, 'PENALTY', 0, { x: dir * (pitch.length / 2 - pitch.penaltySpotDist), y: 0 });
  }),

  spec('fk-center', 'Free kick (central)', 'Direct range, dead centre — the wall forms in front of you.', 5, 'defense', 0, (s, cfg) => {
    freeKickAt(s, cfg, 0, PITCH[s.mode.teamSize].length * 0.16, 0);
  }),

  spec('fk-wide', 'Free kick (wide)', 'Cross range, off the flank. Hold loft and whip it in.', 5, 'defense', 0, (s, cfg) => {
    const pitch = PITCH[s.mode.teamSize];
    freeKickAt(s, cfg, 0, pitch.length * 0.24, pitch.width * 0.3);
  }),

  spec('corner', 'Corner', 'From the flag. Everyone is in the box.', 5, 'defense', 0, (s, cfg) => {
    const pitch = PITCH[s.mode.teamSize];
    const dir = dirOf(s, 0);
    keepersHome(s);
    scatter(s, 0, 0.32);
    scatter(s, 1, -0.36);
    startRestart(s, [], cfg, 'CORNER', 0, { x: (dir * pitch.length) / 2, y: pitch.width / 2 });
  }),

  spec('kickoff', 'Kick-off', 'The whole restart, from the whistle.', 5, 'defense', 0, (s, cfg) => {
    keepersHome(s);
    startKickoff(s, 0, [], cfg);
  }),
];

export function drillById(id: string): DrillSpec | null {
  return DRILLS.find((d) => d.id === id) ?? null;
}
